import React, { useEffect, useState } from "react";
import { Loader2, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function StaffManager() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff");
      setStaff(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/staff", form);
      toast.success("Staff member added");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this staff member?")) return;
    try {
      await api.delete(`/staff/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="staff-manager">
      <div className="brut-card-static p-6">
        <h2 className="font-display font-black text-2xl mb-4">Add new staff</h2>
        <form onSubmit={onCreate} className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-1.5">Name</label>
            <input className="brut-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-1.5">Email</label>
            <input type="email" className="brut-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-1.5">Password (min 8)</label>
            <input type="password" className="brut-input" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" />
          </div>
          <button type="submit" disabled={creating} className="brut-btn-primary w-full" data-testid="staff-create-btn">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4" /> Add staff</>}
          </button>
        </form>
      </div>

      <div className="brut-card-static p-6">
        <h2 className="font-display font-black text-2xl mb-4">Team ({staff.length})</h2>
        {user?.role !== "admin" && (
          <p className="text-xs text-zinc-600 mb-3 font-medium">
            Only admins can remove team members. Contact an admin to revoke access.
          </p>
        )}
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <ul className="space-y-2" data-testid="staff-list">
            {staff.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 border-2 border-black rounded-lg px-3 py-2 bg-white">
                <div className="min-w-0">
                  <div className="font-bold text-sm truncate">{s.name} <span className="brut-tag bg-[#FEF3C7] ml-1">{s.role}</span></div>
                  <div className="text-xs text-zinc-600 truncate">{s.email}</div>
                </div>
                {user?.role === "admin" && s.id !== user?.id && s.role !== "admin" && (
                  <button onClick={() => onDelete(s.id)} className="text-[#C8102E] hover:text-black p-1" data-testid={`staff-delete-${s.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
